import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Calendar, Clock, MapPin, ChevronLeft, ChevronRight, X } from "lucide-react"
import { Button } from "../components/ui/button.tsx"
import { ProfileLayout } from "../components/ProfileLayout"
import { Pagination } from "../components/Pagination"
import api from "../utils/api"
import { getCookie } from "../utils/cookies"
import { format } from "date-fns"

// Helper function to convert string to Title Case
function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export default function MyBookingsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "")
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all")
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 10

  // Reschedule modal state
  const [showReschedule, setShowReschedule] = useState(false)
  const [rescheduleBooking, setRescheduleBooking] = useState(null)
  const [rescheduleDoctors, setRescheduleDoctors] = useState([])
  const [rescheduleSelectedDoctor, setRescheduleSelectedDoctor] = useState("")
  const [rescheduleDates, setRescheduleDates] = useState([])
  const [rescheduleSlots, setRescheduleSlots] = useState([])
  const [rescheduleDateOffset, setRescheduleDateOffset] = useState(0)
  const [rescheduleSelectedDate, setRescheduleSelectedDate] = useState(null)
  const [rescheduleSelectedSlot, setRescheduleSelectedSlot] = useState("")
  const [rescheduleSelectedSlotRaw, setRescheduleSelectedSlotRaw] = useState("")
  const [rescheduleReason, setRescheduleReason] = useState("")
  const [rescheduleLoading, setRescheduleLoading] = useState(false)
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false)
  const [rescheduleDoctorPrice, setRescheduleDoctorPrice] = useState(0)
  const [reschedulePriceLoading, setReschedulePriceLoading] = useState(false)
  const DATES_PER_PAGE = 5

  const formatTime = (time) => {
    if (!time) return ""
    const [h, m] = time.split(":")
    let hour = parseInt(h)
    const ampm = hour >= 12 ? "PM" : "AM"
    hour = hour % 12 || 12
    return `${hour}:${m} ${ampm}`
  }

  const generateSampleDates = () => {
    const dates = []
    const today = new Date()
    for (let i = 0; i < 10; i++) {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      dates.push(d)
    }
    return dates
  }

  const generateSampleSlots = () => [
    { slno: 1, slottime: "09:00", isavailable: "Y" },
    { slno: 2, slottime: "09:30", isavailable: "Y" },
    { slno: 3, slottime: "10:00", isavailable: "N" },
    { slno: 4, slottime: "10:30", isavailable: "Y" },
    { slno: 5, slottime: "11:00", isavailable: "Y" },
    { slno: 6, slottime: "11:30", isavailable: "N" },
    { slno: 7, slottime: "12:00", isavailable: "Y" },
    { slno: 8, slottime: "14:00", isavailable: "Y" },
    { slno: 9, slottime: "14:30", isavailable: "Y" },
    { slno: 10, slottime: "15:00", isavailable: "Y" },
    { slno: 11, slottime: "15:30", isavailable: "N" },
    { slno: 12, slottime: "16:00", isavailable: "Y" },
  ]

  const openReschedule = (booking) => {
    setRescheduleBooking(booking)
    setRescheduleDoctors([])
    setRescheduleSelectedDoctor("")
    setRescheduleSlots([])
    setRescheduleDates([])
    setRescheduleDateOffset(0)
    setRescheduleSelectedDate(null)
    setRescheduleSelectedSlot("")
    setRescheduleSelectedSlotRaw("")
    setRescheduleReason("")
    setRescheduleDoctorPrice(0)
    setShowReschedule(true)
    setTimeout(() => setRescheduleSelectedDoctor(booking.doctorId || ""), 0)

    if (booking.doctorId && booking.hospitalId) {
      api.get(`/apis/web/relateddoctors?doctorId=${booking.doctorId}&hospitalId=${booking.hospitalId}`)
        .then(res => {
          const doctors = Array.isArray(res.data.data) ? res.data.data : Array.isArray(res.data.result) ? res.data.result : []
          setRescheduleDoctors(doctors)
        })
        .catch(() => setRescheduleDoctors([]))
    }


  }

  // Fetch doctor consultation price on doctor change
  useEffect(() => {
    if (!rescheduleSelectedDoctor || !rescheduleBooking?.hospitalId || !rescheduleBooking?.mrNo) return
    setReschedulePriceLoading(true)
    api.get(`/apis/web/fullconsultationprice?doctorId=${rescheduleSelectedDoctor}&mrnumber=${rescheduleBooking.mrNo}&hospitalId=${rescheduleBooking.hospitalId}`)
      .then(res => {
        const price = parseFloat(res.data?.result?.consultationCharge || 0)
        setRescheduleDoctorPrice(price)
      })
      .catch(() => setRescheduleDoctorPrice(0))
      .finally(() => setReschedulePriceLoading(false))
  }, [rescheduleSelectedDoctor])

  useEffect(() => {
    if (!rescheduleSelectedDoctor || !rescheduleBooking?.hospitalId) return
    setRescheduleDates([])
    setRescheduleSlots([])
    setRescheduleSelectedDate(null)
    setRescheduleSelectedSlot("")
    setRescheduleSelectedSlotRaw("")
    setRescheduleDateOffset(0)
    api.get(`/apis/web/doctoravaildates?clnorgcode=${rescheduleBooking.hospitalId}&doctorId=${rescheduleSelectedDoctor}`)
      .then(res => {
        const dates = Array.isArray(res.data.result) ? res.data.result : []
        setRescheduleDates(dates)
        if (dates.length > 0) {
          setRescheduleSelectedDate(new Date(dates[0].dateTime + "T00:00:00"))
        }
      })
      .catch(() => setRescheduleDates([]))
  }, [rescheduleSelectedDoctor])

  useEffect(() => {
    if (!rescheduleBooking || !rescheduleSelectedDate) return

    if (rescheduleBooking.hospitalId) {
      setRescheduleSlotsLoading(true)
      const doctorId = rescheduleSelectedDoctor || rescheduleBooking.doctorId
      const formattedDate = format(rescheduleSelectedDate, "yyyy-MM-dd")
      api.get(`/apis/web/doctavailtimeslots?clnorgcode=${rescheduleBooking.hospitalId}&doctorId=${doctorId}&date=${formattedDate}`)
        .then(res => {
          const data = res.data
          if (data.status === 0 && Array.isArray(data.result)) {
            setRescheduleSlots(data.result)
          } else {
            setRescheduleSlots([])
          }
        })
        .catch(() => setRescheduleSlots([]))
        .finally(() => setRescheduleSlotsLoading(false))
    }
  }, [rescheduleBooking, rescheduleSelectedDate, rescheduleSelectedDoctor])

  const paidAmount = rescheduleBooking?.paidAmount || 0
  const priceDiff = rescheduleDoctorPrice - paidAmount
  const needsPayment = priceDiff > 0
  const hasRefund = priceDiff < 0

  const handleRescheduleConfirm = async () => {
    if (!rescheduleSelectedDate || !rescheduleBooking) return
    const isVideo = rescheduleBooking.consTypeCode === "006"
    if (!isVideo && !rescheduleSelectedSlotRaw) return
    setRescheduleLoading(true)
    try {
      if (rescheduleBooking.status === "pending") {
        const isVideo = rescheduleBooking.consTypeCode === "006"
        const apptTime = isVideo ? "00:00" : rescheduleSelectedSlotRaw
        const consType = isVideo ? "006" : "001"

        if (priceDiff === 0) {
          const res = await api.post("/apis/web/reschedulesamedoctorappointment", {
            appointmentNo: rescheduleBooking.id,
            appointmentDate: format(rescheduleSelectedDate, "yyyy-MM-dd"),
            appointmentTime: apptTime,
            doctorCode: rescheduleSelectedDoctor,
            clnorgcode: rescheduleBooking.hospitalId,
            userId: rescheduleBooking.mrNo,
          })
          if (res.data?.success || res.data?.status === 0) {
            await fetchBookings()
            setShowReschedule(false)
            alert("Appointment rescheduled successfully!")
          } else {
            alert(res.data?.message || "Failed to reschedule")
          }
        } else {
          const selectedDoc = rescheduleDoctors.find(d => d.DOCTORID === rescheduleSelectedDoctor)
          let userMobile = ""
          try {
            const u = JSON.parse(localStorage.getItem("user") || getCookie("user") || "{}")
            userMobile = u?.mobile || u?.phone || u?.mobileNo || ""
          } catch {}

          await api.post("/apis/web/cancelofflineappointment", {
            appointmentNo: rescheduleBooking.id,
            cancelledBy: rescheduleBooking.mrNo,
            Remarks: rescheduleReason,
          })

          const orderRes = await api.post("/apis/web/createorder", {
            amount: rescheduleDoctorPrice,
            patientId: rescheduleBooking.mrNo,
            doctorId: rescheduleSelectedDoctor,
          })
          const orderId = orderRes.data?.orderId || ""

          const apptRes = await api.post("/apis/web/createappointment", {
            doctorCode: rescheduleSelectedDoctor,
            doctorName: selectedDoc?.DOCTOR || rescheduleBooking.doctorName,
            patientId: rescheduleBooking.mrNo,
            patientName: rescheduleBooking.patientName,
            amount: rescheduleDoctorPrice,
            appointmentDate: format(rescheduleSelectedDate, "yyyy-MM-dd"),
            appointmentTime: apptTime,
            visit_type: "1",
            consType: consType,
            clnorgcode: rescheduleBooking.hospitalId,
            hospitalName: rescheduleBooking.location,
            userId: rescheduleBooking.mrNo,
            mobile: userMobile,
            razorpayPaymentId: "",
            razorpayOrderId: "",
            razorpaySignature: "",
            orderId: orderId,
            paymentStatus: "Pending",
          })
          if (apptRes.data?.success || apptRes.data?.status === 0) {
            await fetchBookings()
            setShowReschedule(false)
            alert("Appointment rescheduled successfully!")
          } else {
            alert(apptRes.data?.message || "Failed to reschedule")
          }
        }
      } else if (rescheduleBooking.status === "paid") {
        const apptTime = isVideo ? "00:00" : rescheduleSelectedSlotRaw
        const consType = isVideo ? "006" : "001"

        if (priceDiff === 0) {
          // Paid + same price (same or different doctor) — just reschedule
          const res = await api.post("/apis/web/reschedulesamedoctorappointment", {
            appointmentNo: rescheduleBooking.id,
            appointmentDate: format(rescheduleSelectedDate, "yyyy-MM-dd"),
            appointmentTime: apptTime,
            doctorCode: rescheduleSelectedDoctor,
            clnorgcode: rescheduleBooking.hospitalId,
            userId: rescheduleBooking.mrNo,
          })
          if (res.data?.success || res.data?.status === 0) {
            await fetchBookings()
            setShowReschedule(false)
            alert("Appointment rescheduled successfully!")
          } else {
            alert(res.data?.message || "Failed to reschedule")
          }
        } else if (priceDiff > 0) {
          // Same doctor, higher price — pay difference via Razorpay then call rescheduledpaidappointment
          const orderRes = await api.post("/apis/web/createorder", {
            amount: priceDiff,
            patientId: rescheduleBooking.mrNo,
            doctorId: rescheduleSelectedDoctor,
          })
          const orderData = orderRes.data
          if (orderData.status === 0 && orderData.orderId) {
            const options = {
              key: process.env.REACT_APP_RAZORPAY_KEY,
              name: "Pranaam Hospitals",
              description: `Reschedule - ${rescheduleBooking.doctorName}`,
              image: "/images/pranaam-logo.png",
              order_id: orderData.orderId,
              prefill: {
                name: rescheduleBooking.patientName,
                contact: rescheduleBooking.mobile,
              },
              handler: async function (response) {
                try {
                  const res = await api.post("/apis/web/rescheduledpaidappointment", {
                    oldAppointmentNo: rescheduleBooking.id,
                    cancelledBy: rescheduleBooking.mrNo,
                    clnorgcode: rescheduleBooking.hospitalId,
                    doctorCode: rescheduleSelectedDoctor,
                    mobile: rescheduleBooking.mobile,
                    patientId: rescheduleBooking.mrNo,
                    newAmount: rescheduleDoctorPrice,
                    userId: rescheduleBooking.mrNo,
                    appointmentDate: format(rescheduleSelectedDate, "yyyy-MM-dd"),
                    appointmentTime: apptTime,
                    visit_type: "1",
                    consType: consType,
                    razorpayOrderId: response.razorpay_order_id,
                    razorpayPaymentId: response.razorpay_payment_id,
                    razorpaySignature: response.razorpay_signature,
                    refundId: "",
                  })
                  if (res.data?.success || res.data?.status === 0) {
                    await fetchBookings()
                    setShowReschedule(false)
                    alert("Appointment rescheduled successfully!")
                  } else {
                    alert(res.data?.message || "Failed to reschedule")
                  }
                } catch {
                  alert("Failed to reschedule appointment")
                }
                setRescheduleLoading(false)
              },
              modal: {
                ondismiss: function () {
                  alert("Payment cancelled.")
                  setRescheduleLoading(false)
                },
              },
              theme: { color: "#dc2626" },
            }
            const rzp = new window.Razorpay(options)
            rzp.on("payment.failed", function (response) {
              alert(response.error?.description || "Payment failed. Please try again.")
              setRescheduleLoading(false)
            })
            rzp.open()
            return // don't set loading false here, handler will do it
          } else {
            alert(orderData.message || "Failed to create order")
          }
        } else if (priceDiff < 0) {
          // Paid more than new doctor's price — refund difference then reschedule
          const refundAmount = Math.abs(priceDiff)
          const orderDetailsRes = await api.get(`/apis/web/getAppointmentsordedetails?APPOINTMENTNO=${rescheduleBooking.id}`)
          const orderResult = Array.isArray(orderDetailsRes.data?.result) ? orderDetailsRes.data.result[0] : orderDetailsRes.data?.result
          const paymentId = orderResult?.PAYMENTID || orderResult?.paymentId || ""
          if (!paymentId) {
            alert("Unable to fetch payment details for refund.")
            setRescheduleLoading(false)
            return
          }
          const refundRes = await api.post("/apis/web/refundpayment", {
            paymentId: paymentId,
            amount: refundAmount,
          })
          const refundData = refundRes.data
          if (refundData?.status === 0 || refundData?.success) {
            const refundId = refundData.refundId || refundData.result?.refundId || ""
            const res = await api.post("/apis/web/rescheduledpaidappointment", {
              oldAppointmentNo: rescheduleBooking.id,
              cancelledBy: rescheduleBooking.mrNo,
              clnorgcode: rescheduleBooking.hospitalId,
              doctorCode: rescheduleSelectedDoctor,
              mobile: rescheduleBooking.mobile,
              patientId: rescheduleBooking.mrNo,
              newAmount: rescheduleDoctorPrice,
              userId: rescheduleBooking.mrNo,
              appointmentDate: format(rescheduleSelectedDate, "yyyy-MM-dd"),
              appointmentTime: apptTime,
              visit_type: "1",
              consType: consType,
              refundId: refundId,
            })
            if (res.data?.success || res.data?.status === 0) {
              await fetchBookings()
              setShowReschedule(false)
              alert(`Appointment rescheduled successfully! ₹${refundAmount} will be refunded within 5–7 working days.`)
            } else {
              alert(res.data?.message || "Failed to reschedule")
            }
          } else {
            alert(refundData?.message || "Refund failed. Please try again.")
          }
        }
      }
    } catch {
      alert("Failed to reschedule appointment")
    }
    setRescheduleLoading(false)
  }

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelBooking, setCancelBooking] = useState(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelReason, setCancelReason] = useState("")

  const allRescheduleDates = rescheduleDates.map(item => new Date(item.dateTime + "T00:00:00"))
  const visibleRescheduleDates = allRescheduleDates.slice(rescheduleDateOffset * DATES_PER_PAGE, (rescheduleDateOffset + 1) * DATES_PER_PAGE)

  const fetchBookings = async () => {
    try {
      const userStr = localStorage.getItem("user") || getCookie("user")
      if (!userStr || userStr === "undefined") { navigate("/"); return }
      const user = JSON.parse(userStr)
      let mobile = user?.mobile || user?.phone || ""
      if (!mobile) return
      if (!mobile.startsWith("91")) mobile = `91${mobile}`
      const response = await api.get(`/apis/web/myappointments?mobile=${mobile}`)
      if (response.data?.result && Array.isArray(response.data.result)) {
        setBookings(response.data.result.map((item, index) => {
          let formattedTime = ""
          const rawTime = item.APPOINTMENTTIME || ""
          if (rawTime && rawTime !== "00:00") {
            const [h, m] = rawTime.split(":")
            const hour = parseInt(h)
            if (!isNaN(hour)) {
              const ampm = hour >= 12 ? "PM" : "AM"
              formattedTime = `${hour % 12 || 12}:${m} ${ampm}`
            }
          }
          return {
            id: item.APPOINTMENTNO || index + 1,
            consultNo: item.CONSULTNO || "",
            patientName: toTitleCase(item.PATIENT_NAME?.trim() || ""),
            mrNo: item.PatientMr_No || "",
            doctorName: toTitleCase(item.DOCTNAME?.trim() || ""),
            department: toTitleCase(item.DEPTNAME || ""),
            consType: item.CONSTYPE === "006" ? "Video Consultation" : "General Consultation",
            consTypeCode: item.CONSTYPE || "001",
            date: item.APPOINTMENTDATE || "",
            time: formattedTime,
            rawTime: item.APPOINTMENTTIME || "",
            location: item.HospitalName || "",
            gender: item.GENDER || "",
            age: item.AGE || "",
            bill: item.VISITAMOUNT ? `₹${item.VISITAMOUNT}` : "",
            paidAmount: parseFloat(item.VISITAMOUNT) || 0,

            status: item.CONSULTED === "Y" ? "completed" : (item.VISITSTATUS || "").toLowerCase(),
            doctorId: item.DOCTORID || item.DOCTID || item.doctcode || "",
            hospitalId: item.HOSPITALID || item.Hospital_Id || item.CLNORGCODE || "",
            mobile: item.MOBILE || "",
            isRescheduled: item.isresh === "Y"
          }
        }))
      }
    } catch (error) {
      console.error("Error fetching bookings:", error)
    }
  }

  useEffect(() => {
    fetchBookings().finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    const params = {}
    if (searchQuery) params.search = searchQuery
    if (statusFilter !== "all") params.status = statusFilter
    setSearchParams(params)
  }, [searchQuery, statusFilter, setSearchParams])

  const filteredBookings = bookings.filter(booking => {
    const locationFilter = searchParams.get("location")
    const specialtyFilter = searchParams.get("specialty")
    const doctorFilter = searchParams.get("doctor")
    
    const matchesLocation = !locationFilter || booking.location === locationFilter
    const matchesSpecialty = !specialtyFilter || booking.department.toLowerCase().includes(specialtyFilter.toLowerCase())
    const matchesDoctor = !doctorFilter || booking.doctorName.toLowerCase().includes(doctorFilter.toLowerCase())
    
    const matchesSearch = 
      booking.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.doctorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.department.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter
    
    return matchesLocation && matchesSpecialty && matchesDoctor && matchesSearch && matchesStatus
  })

  const getStatusColor = (status) => {
    switch(status) {
      case "paid": return "bg-green-50 text-green-700 border-green-200"
      case "completed": return "bg-blue-50 text-blue-700 border-blue-200"
      case "cancelled": return "bg-red-50 text-red-700 border-red-200"
      case "pending": return "bg-yellow-50 text-yellow-700 border-yellow-200"
      default: return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-red-600" />
              <h1 className="text-2xl font-bold text-gray-900">My Appointments</h1>
            </div>
            <Button className="gap-2 w-full sm:w-auto" onClick={() => navigate("/book-now")}>
              <Calendar className="w-4 h-4" />
              Book an Appointment
            </Button>
          </div>
        </div>

        <div className="px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading your bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 text-gray-500 font-medium">No bookings found.</div>
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredBookings.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{booking.patientName}</h3>
                      <p className="text-sm text-gray-600">{booking.mrNo}</p>
                      <p className="text-sm text-gray-600">{booking.doctorName} | {booking.department}</p>
                      <p className="text-xs font-medium mt-1">
                        <span className={booking.consType === "Video Consultation" ? "text-blue-600" : "text-green-600"}>{booking.consType}</span>
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-md text-sm font-medium border ${getStatusColor(booking.status)}`}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{booking.date}</span>
                    {booking.time && <><Clock className="w-4 h-4 ml-4" /><span>{booking.time}</span></>}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{booking.location}</span>
                    {booking.bill && <span className="ml-auto font-medium text-gray-900">Bill: {booking.bill}</span>}
                  </div>
                </div>

                {booking.status !== "cancelled" && (() => {
                  // Hide buttons if appointment is within 30 min or already passed
                  const parts = (booking.date || "").split("/")
                  const [hh, mm] = (booking.rawTime || "00:00").split(":")
                  if (parts.length === 3) {
                    const apptDate = new Date(parts[2], parseInt(parts[1], 10) - 1, parseInt(parts[0], 10), parseInt(hh) || 0, parseInt(mm) || 0)
                    const now = new Date()
                    if (apptDate.getTime() - now.getTime() < 30 * 60 * 1000) return null
                  }
                  return (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                    {!booking.isRescheduled && (
                    <button
                      onClick={() => openReschedule(booking)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition-colors"
                    >
                      Reschedule
                    </button>
                    )}
                    <button
                      onClick={() => { setCancelBooking(booking); setCancelReason(""); setShowCancelModal(true) }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-600 hover:text-white transition-colors"
                    >
                      Cancel Appointment
                    </button>
                  </div>
                  )
                })()}  
              </div>
            ))}
            </div>
            <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredBookings.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </>
            )}
          </div>
        </div>
      </div>
      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white rounded-xl w-[90vw] max-w-[380px] p-6 shadow-xl" style={{ zIndex: 51 }}>
            <div className="text-center">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Appointment</h3>
              <p className="text-sm text-gray-600 mb-4">Are you sure you want to cancel this appointment?</p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none mb-6"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  No
                </button>
                <button
                  disabled={cancelLoading || !cancelReason.trim()}
                  onClick={async () => {
                    setCancelLoading(true)
                    try {
                      let userId = ""
                      try {
                        const u = JSON.parse(localStorage.getItem("user") || getCookie("user") || "{}")
                        userId = u?.patientId || u?.userId || u?.mobile || ""
                      } catch {}
                      const res = await api.post("/apis/web/cancelappointment", { appointmentNo: cancelBooking.id, cancelledBy: userId, Remarks: cancelReason })
                      if (res.data?.success || res.data?.status === 0) {
                        setBookings(prev => prev.map(b => b.id === cancelBooking.id ? { ...b, status: "cancelled" } : b))
                        setShowCancelModal(false)
                        if (cancelBooking.status === "paid") {
                          alert("Appointment cancelled. Refund within 5–7 working days.")
                        }
                      } else {
                        alert(res.data?.message || "Failed to cancel appointment")
                      }
                    } catch {
                      alert("Failed to cancel appointment")
                    }
                    setCancelLoading(false)
                  }}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cancelLoading ? "Cancelling..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Reschedule Modal */}
      {showReschedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowReschedule(false)} />
          <div className="relative bg-white rounded-xl w-[95vw] max-w-[500px] max-h-[85vh] overflow-y-auto shadow-xl" style={{ zIndex: 51 }}>
            <button
              onClick={() => setShowReschedule(false)}
              className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-5 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 pr-8">Reschedule Appointment</h2>
              <p className="text-sm text-gray-600 mb-1">{rescheduleBooking?.doctorName} | {rescheduleBooking?.department}</p>
              <p className="text-xs text-gray-500 mb-4">{rescheduleBooking?.patientName} &middot; {rescheduleBooking?.location}</p>

              {/* Doctor Dropdown */}
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Select Doctor</h3>
              <select
                value={rescheduleSelectedDoctor}
                onChange={(e) => setRescheduleSelectedDoctor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary mb-4"
              >
                <option value="" disabled>Select Doctor</option>
                {rescheduleDoctors.map((doc) => (
                  <option key={doc.DOCTORID} value={doc.DOCTORID}>{doc.DOCTOR}</option>
                ))}
              </select>

              {/* Date Picker */}
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Select Date</h3>
              <div className="flex items-center gap-1 mb-4">
                <button
                  onClick={() => setRescheduleDateOffset(Math.max(0, rescheduleDateOffset - 1))}
                  disabled={rescheduleDateOffset === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex-1 flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                  {visibleRescheduleDates.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center w-full py-2">No dates available</p>
                  ) : (
                    visibleRescheduleDates.map((date, index) => {
                      const isSelected = rescheduleSelectedDate && rescheduleSelectedDate.toDateString() === date.toDateString()
                      const isToday = date.toDateString() === new Date().toDateString()
                      return (
                        <button
                          key={index}
                          onClick={() => { setRescheduleSelectedDate(date); setRescheduleSelectedSlot(""); setRescheduleSelectedSlotRaw("") }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                            isSelected
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {isToday ? "Today" : format(date, "EEE")}, {format(date, "dd MMM")}
                        </button>
                      )
                    })
                  )}
                </div>

                <button
                  onClick={() => setRescheduleDateOffset(rescheduleDateOffset + 1)}
                  disabled={((rescheduleDateOffset + 1) * DATES_PER_PAGE) >= allRescheduleDates.length}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Time Slots */}
              {rescheduleBooking?.consTypeCode !== "006" && (
              <>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Select Time</h3>
              {!rescheduleSelectedDate ? (
                <p className="text-sm text-gray-400 text-center py-4">Please select a date first</p>
              ) : rescheduleSlotsLoading ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : rescheduleSlots.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No slots available</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
                  {rescheduleSlots.map((slot, index) => {
                    const time = formatTime(slot.slottime)
                    const rawTime = slot.slottime?.substring(0, 5) || slot.slottime
                    return (
                      <button
                        key={slot.slno || index}
                        disabled={slot.isavailable !== "Y"}
                        onClick={() => { setRescheduleSelectedSlot(time); setRescheduleSelectedSlotRaw(rawTime) }}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          rescheduleSelectedSlot === time
                            ? "bg-primary text-white"
                            : slot.isavailable === "Y"
                            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        {time}
                      </button>
                    )
                  })}
                </div>
              )}
              </>
              )}

              {/* Reason */}
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Enter reason for rescheduling</h3>
              <textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Enter reason for rescheduling..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-4"
              />

              {/* Doctor Price Info */}
              {reschedulePriceLoading ? (
                <p className="text-xs text-gray-400 mb-2">Fetching doctor fee...</p>
              ) : rescheduleDoctorPrice > 0 && rescheduleBooking?.status === "pending" ? (
                <div className="bg-gray-50 rounded-lg px-3 py-2 mb-2 text-sm space-y-1">
                  {rescheduleSelectedDoctor !== rescheduleBooking?.doctorId ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Earlier Appointment: {rescheduleBooking?.doctorName}</span>
                        <span className="font-semibold text-gray-900">₹{paidAmount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Updated Appointment: {rescheduleDoctors.find(d => d.DOCTORID === rescheduleSelectedDoctor)?.DOCTOR || ""}</span>
                        <span className="font-semibold text-primary">₹{rescheduleDoctorPrice}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Doctor Fee:</span>
                      <span className="font-semibold text-gray-900">₹{rescheduleDoctorPrice}</span>
                    </div>
                  )}
                </div>
              ) : rescheduleDoctorPrice > 0 && (
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-2 text-sm">
                  <span className="text-gray-600">Doctor Fee: <span className="font-semibold text-gray-900">₹{rescheduleDoctorPrice}</span></span>
                  <span className="text-gray-600">Paid: <span className="font-semibold text-gray-900">₹{paidAmount}</span></span>
                  {priceDiff !== 0 && (
                    <span className={priceDiff > 0 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                      {priceDiff > 0 ? `Due: ₹${priceDiff}` : `Refund: ₹${Math.abs(priceDiff)}`}
                    </span>
                  )}
                </div>
              )}

              {/* Confirm Button */}
              <button
                onClick={handleRescheduleConfirm}
                disabled={(!rescheduleSelectedSlotRaw && rescheduleBooking?.consTypeCode !== "006") || rescheduleLoading}
                className="w-full mt-2 px-4 py-3 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rescheduleLoading ? "Rescheduling..." : rescheduleBooking?.status !== "pending" && needsPayment ? `Pay ₹${priceDiff} & Reschedule` : rescheduleBooking?.status !== "pending" && hasRefund ? `Refund ₹${Math.abs(priceDiff)} & Reschedule` : "Confirm Reschedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ProfileLayout>
  )
}
